import React, { useState, useEffect, useMemo, useRef } from "react";
import ContactVendorleadsModal from "../../components/ContactVendorleadsModal";
import SignInModal from "../../components/SignInModal";
import Navbar from "../../components/Navbar";

const BRAND_COLOR = "#1a3f6b";
const BRAND_ACCENT_COLOR = "#10b981";
const BRAND_BLUE = "#3b82f6";
const GRAY_DARK_COLOR = "#374151";
const DARK_GRADIENT = "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #134e4a 100%)";
const FONT_STACK = "Inter, Arial, sans-serif";

/* ─────────── Event config (mirrors platform Map Prospector) ─────────── */
const EVENT_META = {
  property_analysed: { label: "Property Analysed", color: "#7c3aed", icon: "📊" },
  view_appraisal:    { label: "Viewed Appraisal",  color: "#be123c", icon: "👁" },
  send_property_report: { label: "Report Sent",    color: "#6d28d9", icon: "📄" },
  click_track_property: { label: "Tracked Property", color: "#be185d", icon: "♥" },
  Open:              { label: "Opened Email",      color: "#047857", icon: "✉" },
  Click:             { label: "Clicked Email",     color: "#4338ca", icon: "↗" },
  sms_sent:          { label: "SMS Sent",          color: "#166534", icon: "✆" },
};

/* ─────────── Sample addresses on the map ─────────── */
const mk = (id, addr, x, y, events) => ({ id, addr, suburb: "Manly NSW", x, y, events });
const ADDRESSES = [
  mk("a1",  "22 Sevira Avenue",     38, 34, [
    { type: "property_analysed", who: "margaret.w@gmail.com", when: "2 hours ago" },
    { type: "Open",              who: "margaret.w@gmail.com", when: "Yesterday", subject: "Your June market update" },
    { type: "view_appraisal",    who: "margaret.w@gmail.com", when: "3 days ago" },
    { type: "Click",             who: "margaret.w@gmail.com", when: "1 week ago", subject: "3 homes sold near you" },
  ]),
  mk("a2",  "47 Banks Avenue",      52, 28, [
    { type: "property_analysed", who: "d.chen@outlook.com", when: "5 hours ago" },
    { type: "Open",              who: "d.chen@outlook.com", when: "2 days ago", subject: "Your June market update" },
    { type: "Click",             who: "d.chen@outlook.com", when: "1 week ago" },
  ]),
  mk("a3",  "8 Camden Valley Way",  44, 48, [
    { type: "view_appraisal",    who: "priya.a@gmail.com", when: "1 day ago" },
    { type: "Open",              who: "priya.a@gmail.com", when: "4 days ago" },
  ]),
  mk("a4",  "5 Ridgeline Drive",    62, 38, [
    { type: "property_analysed", who: "reilly.fam@bigpond.com", when: "6 hours ago" },
    { type: "Click",             who: "reilly.fam@bigpond.com", when: "3 days ago" },
    { type: "sms_sent",          who: "reilly.fam@bigpond.com", when: "5 days ago" },
  ]),
  mk("a5",  "31 Heritage Way",      30, 56, [
    { type: "Open",              who: "evasquez@yahoo.com", when: "1 day ago" },
    { type: "view_appraisal",    who: "evasquez@yahoo.com", when: "5 days ago" },
  ]),
  mk("a6",  "12 Banksia Street",    56, 60, [
    { type: "Open",              who: "j.patel@gmail.com", when: "2 days ago" },
  ]),
  mk("a7",  "18 Garrick Road",      70, 52, [
    { type: "Open",              who: "h.lin@gmail.com", when: "2 weeks ago" },
  ]),
  mk("a8",  "7 Lakeside Crescent",  74, 32, [
    { type: "property_analysed", who: "rob.mancini@gmail.com", when: "3 days ago" },
    { type: "Open",              who: "rob.mancini@gmail.com", when: "1 week ago" },
  ]),
  mk("a9",  "3 Currawong Place",    36, 68, [
    { type: "view_appraisal",    who: "o.tran@outlook.com", when: "5 days ago" },
  ]),
  mk("a10", "29 Sevira Avenue",     42, 24, [
    { type: "Open",              who: "greg.nelson@bigpond.com", when: "1 day ago" },
    { type: "Click",             who: "greg.nelson@bigpond.com", when: "1 week ago" },
  ]),
  mk("a11", "2 Maple Court",        18, 36, [
    { type: "property_analysed", who: "frank.r@gmail.com", when: "8 hours ago" },
    { type: "Open",              who: "frank.r@gmail.com", when: "4 days ago" },
  ]),
  mk("a12", "44 Highview Road",     32, 22, [
    { type: "view_appraisal",    who: "m.rossi@gmail.com", when: "2 days ago" },
    { type: "Open",              who: "m.rossi@gmail.com", when: "1 week ago" },
  ]),
];

/* ─────────── Animations ─────────── */
function GlobalAnimations() {
  return (
    <style>{`
      @keyframes mpFadeUp { 0% { opacity: 0; transform: translateY(10px); } 100% { opacity: 1; transform: translateY(0); } }
      @keyframes mpFadeIn { 0% { opacity: 0; } 100% { opacity: 1; } }
      @keyframes mpSlideInRight { 0% { opacity: 0; transform: translateX(16px); } 100% { opacity: 1; transform: translateX(0); } }
      @keyframes mpSlideInLeft { 0% { opacity: 0; transform: translateX(-16px); } 100% { opacity: 1; transform: translateX(0); } }
      @keyframes mpPulseRing { 0% { transform: translate(-50%, -50%) scale(0.6); opacity: 0.8; } 100% { transform: translate(-50%, -50%) scale(2.4); opacity: 0; } }
      @keyframes mpPinDrop { 0% { transform: translate(-50%, -160%) scale(0.5); opacity: 0; } 60% { transform: translate(-50%, -95%) scale(1.08); opacity: 1; } 100% { transform: translate(-50%, -100%) scale(1); opacity: 1; } }
      @keyframes mpDot { 0%, 100% { opacity: 0.2; transform: scale(0.85); } 50% { opacity: 1; transform: scale(1.1); } }
      @keyframes mpCardPop { 0% { transform: translate(-50%, -110%) scale(0.85); opacity: 0; } 100% { transform: translate(-50%, -110%) scale(1); opacity: 1; } }
    `}</style>
  );
}

/* ─────────── Sample map background ─────────── */
function MapBaseLayer({ width = 600, height = 420 }) {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid slice"
      style={{ position: "absolute", inset: 0, display: "block" }}
    >
      <rect width={width} height={height} fill="#e9eef3" />
      <path d="M0 320 Q 120 290 240 320 T 600 310 L 600 420 L 0 420 Z" fill="#cfe3ef" />
      <ellipse cx="120" cy="110" rx="80" ry="40" fill="#d6ead7" />
      <ellipse cx="480" cy="90" rx="70" ry="36" fill="#d6ead7" />
      <ellipse cx="320" cy="220" rx="60" ry="34" fill="#d6ead7" />
      <ellipse cx="60" cy="240" rx="44" ry="28" fill="#d6ead7" />
      <path d="M -20 380 Q 150 360 300 380 T 620 370" stroke="#9ec9e2" strokeWidth="20" fill="none" opacity="0.65" />
      <g stroke="#ffffff" strokeWidth="10" strokeLinecap="round">
        <line x1="0" y1="160" x2="600" y2="140" />
        <line x1="0" y1="270" x2="600" y2="260" />
        <line x1="180" y1="0" x2="200" y2="420" />
        <line x1="400" y1="0" x2="420" y2="420" />
      </g>
      <g stroke="#ffd44d" strokeWidth="2" strokeDasharray="6 8" strokeLinecap="round" opacity="0.9">
        <line x1="0" y1="160" x2="600" y2="140" />
        <line x1="0" y1="270" x2="600" y2="260" />
        <line x1="180" y1="0" x2="200" y2="420" />
        <line x1="400" y1="0" x2="420" y2="420" />
      </g>
      <g stroke="#ffffff" strokeWidth="5" strokeLinecap="round" opacity="0.9">
        <line x1="0" y1="75" x2="600" y2="65" />
        <line x1="0" y1="220" x2="600" y2="210" />
        <line x1="0" y1="350" x2="600" y2="340" />
        <line x1="90" y1="0" x2="100" y2="420" />
        <line x1="290" y1="0" x2="305" y2="420" />
        <line x1="510" y1="0" x2="520" y2="420" />
      </g>
    </svg>
  );
}

/* ─────────── Pin on map (matches platform style) ─────────── */
function MapPin({ a, focused, onClick }) {
  const count = a.events.length;
  const isHot = count >= 3;
  const color = isHot ? BRAND_COLOR : BRAND_ACCENT_COLOR;
  return (
    <div
      onClick={onClick}
      style={{
        position: "absolute",
        left: `${a.x}%`,
        top: `${a.y}%`,
        transform: "translate(-50%, -100%)",
        cursor: "pointer",
        zIndex: focused ? 30 : 10,
        animation: "mpPinDrop 0.55s ease both",
      }}
    >
      {isHot && (
        <span
          style={{
            position: "absolute",
            left: "50%",
            top: "100%",
            width: 16,
            height: 16,
            borderRadius: "50%",
            background: color,
            animation: "mpPulseRing 1.8s ease-out infinite",
          }}
        />
      )}
      <div style={{ position: "relative" }}>
        <div
          style={{
            width: focused ? 36 : 30,
            height: focused ? 36 : 30,
            borderRadius: "50%",
            background: focused ? BRAND_COLOR : color,
            border: "2px solid #fff",
            boxShadow: focused
              ? "0 0 0 4px rgba(26,63,107,0.3), 0 4px 10px rgba(0,0,0,0.3)"
              : "0 3px 8px rgba(0,0,0,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 13,
            transition: "all 0.2s ease",
          }}
        >
          📍
        </div>
        {count > 1 && (
          <div
            style={{
              position: "absolute",
              top: -6,
              right: -6,
              minWidth: 18,
              height: 18,
              padding: "0 4px",
              borderRadius: 9,
              background: "#ef4444",
              color: "#fff",
              fontSize: 10.5,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1.5px solid #fff",
            }}
          >
            {count > 9 ? "9+" : count}
          </div>
        )}
        <div
          style={{
            width: 0,
            height: 0,
            margin: "-2px auto 0",
            borderLeft: "6px solid transparent",
            borderRight: "6px solid transparent",
            borderTop: `6px solid ${focused ? BRAND_COLOR : color}`,
          }}
        />
      </div>
    </div>
  );
}

/* ─────────── Floating timeline card over pin ─────────── */
function PinTimelineCard({ a }) {
  // Smart-position so the card never spills past the map edges.
  // Horizontal: anchor card left/center/right of pin based on x.
  // Vertical: flip below pin when near the top.
  const flipBelow = a.y < 32;
  let translateX = "-50%";
  let left = `${a.x}%`;
  if (a.x < 22) {
    translateX = "0%";
    left = `calc(${a.x}% - 14px)`;
  } else if (a.x > 78) {
    translateX = "-100%";
    left = `calc(${a.x}% + 14px)`;
  }
  const translateY = flipBelow ? "20%" : "-110%";
  const pointerStyle = flipBelow
    ? {
        position: "absolute",
        left: "50%",
        top: -6,
        transform: "translateX(-50%) rotate(45deg)",
        width: 12,
        height: 12,
        background: "#fff",
        borderLeft: "1px solid #e2e8f0",
        borderTop: "1px solid #e2e8f0",
      }
    : {
        position: "absolute",
        left: "50%",
        bottom: -6,
        transform: "translateX(-50%) rotate(45deg)",
        width: 12,
        height: 12,
        background: "#fff",
        borderRight: "1px solid #e2e8f0",
        borderBottom: "1px solid #e2e8f0",
      };

  return (
    <div
      style={{
        position: "absolute",
        left,
        top: `${a.y}%`,
        transform: `translate(${translateX}, ${translateY})`,
        background: "#fff",
        borderRadius: 12,
        border: "1px solid #e2e8f0",
        boxShadow: "0 18px 40px rgba(15,23,42,0.28)",
        padding: "0.85rem 0.95rem",
        width: 260,
        maxWidth: "calc(100% - 28px)",
        zIndex: 50,
        animation: "mpCardPop 0.35s cubic-bezier(0.2,0.9,0.3,1.3) both",
        pointerEvents: "none",
      }}
    >
      <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>{a.addr}</div>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8 }}>
        {a.suburb} · {a.events.length} event{a.events.length === 1 ? "" : "s"}
      </div>
      {a.events.slice(0, 3).map((e, i) => {
        const m = EVENT_META[e.type] || { label: e.type, color: "#64748b", icon: "•" };
        return (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              padding: "3px 0",
              fontSize: 11,
              color: "#334155",
            }}
          >
            <span style={{ color: m.color, fontSize: 12 }}>{m.icon}</span>
            <span style={{ flex: 1, fontWeight: 600 }}>{m.label}</span>
            <span style={{ color: "#94a3b8", fontSize: 10 }}>{e.when}</span>
          </div>
        );
      })}
      <div style={pointerStyle} />
    </div>
  );
}

/* ─────────── Hero map (animated) ─────────── */
function MobileTimelineCard({ a }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        border: "1px solid #e2e8f0",
        boxShadow: "0 12px 30px rgba(15,23,42,0.15)",
        padding: "0.85rem 0.95rem",
        marginTop: 14,
      }}
    >
      <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>{a.addr}</div>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8 }}>
        {a.suburb} · {a.events.length} event{a.events.length === 1 ? "" : "s"}
      </div>
      {a.events.slice(0, 3).map((e, i) => {
        const m = EVENT_META[e.type] || { label: e.type, color: "#64748b", icon: "•" };
        return (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              padding: "3px 0",
              fontSize: 11,
              color: "#334155",
            }}
          >
            <span style={{ color: m.color, fontSize: 12 }}>{m.icon}</span>
            <span style={{ flex: 1, fontWeight: 600 }}>{m.label}</span>
            <span style={{ color: "#94a3b8", fontSize: 10 }}>{e.when}</span>
          </div>
        );
      })}
    </div>
  );
}

function HeroMap({ isMobile }) {
  const [focusIdx, setFocusIdx] = useState(0);
  const focusOrder = useMemo(() => ["a1", "a4", "a10", "a11", "a2", "a8"], []);

  useEffect(() => {
    const id = setInterval(() => setFocusIdx((i) => (i + 1) % focusOrder.length), 2200);
    return () => clearInterval(id);
  }, [focusOrder.length]);

  const focusedId = focusOrder[focusIdx];
  const focused = ADDRESSES.find((a) => a.id === focusedId);

  return (
    <>
    <div
      style={{
        position: "relative",
        height: isMobile ? 320 : 500,
        userSelect: "none",
        // No overflow:hidden here so the popover can escape the map edges.
      }}
    >
      {/* Clipped map base — only the SVG gets the rounded mask */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: 16,
          overflow: "hidden",
          border: "1px solid rgba(15,23,42,0.08)",
          boxShadow: "0 24px 70px rgba(15,23,42,0.25)",
          background: "#e8eef3",
        }}
      >
        <MapBaseLayer />
      </div>
      {/* Pins + popovers live above the clip so they can overflow */}
      {ADDRESSES.map((a) => (
        <MapPin key={a.id} a={a} focused={focusedId === a.id} />
      ))}
      {focused && !isMobile && <PinTimelineCard a={focused} />}

      {/* Top legend */}
      <div
        style={{
          position: "absolute",
          top: 14,
          left: 14,
          background: "rgba(255,255,255,0.97)",
          backdropFilter: "blur(6px)",
          borderRadius: 10,
          border: "1px solid rgba(15,23,42,0.08)",
          boxShadow: "0 6px 18px rgba(15,23,42,0.1)",
          padding: "0.55rem 0.75rem",
          fontSize: 11,
          color: GRAY_DARK_COLOR,
          fontWeight: 600,
          display: "flex",
          gap: 12,
          alignItems: "center",
          zIndex: 20,
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: BRAND_COLOR,
              border: "1.5px solid #fff",
              boxShadow: "0 0 0 1px rgba(15,23,42,0.1)",
            }}
          />
          Hot
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: BRAND_ACCENT_COLOR,
              border: "1.5px solid #fff",
              boxShadow: "0 0 0 1px rgba(15,23,42,0.1)",
            }}
          />
          Active
        </span>
      </div>

      {/* Bottom status pill */}
      <div
        style={{
          position: "absolute",
          bottom: 14,
          left: 14,
          background: "rgba(15,23,42,0.88)",
          color: "#fff",
          padding: "6px 12px",
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.4,
          display: "flex",
          alignItems: "center",
          gap: 8,
          zIndex: 22,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: BRAND_ACCENT_COLOR,
            boxShadow: `0 0 0 4px ${BRAND_ACCENT_COLOR}33`,
            animation: "mpDot 1.4s ease-in-out infinite",
          }}
        />
        {ADDRESSES.length} addresses with activity
      </div>
    </div>
    {isMobile && focused && <MobileTimelineCard a={focused} />}
    </>
  );
}

/* ─────────── Static pin card for the click section ─────────── */
function StaticTimelineCard({ a }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 14,
        border: "1px solid #e2e8f0",
        boxShadow: "0 16px 40px rgba(15,23,42,0.12)",
        padding: "1.1rem 1.2rem",
        width: "100%",
      }}
    >
      <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>{a.addr}</div>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 14 }}>
        {a.events.length} events
      </div>
      <div>
        {a.events.map((e, i) => {
          const m = EVENT_META[e.type] || { label: e.type, color: "#6b7280", icon: "•" };
          return (
            <div key={i} style={{ display: "flex", gap: 10, marginBottom: 0 }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: 24,
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: `${m.color}15`,
                    color: m.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                  }}
                >
                  {m.icon}
                </div>
                {i < a.events.length - 1 && (
                  <div style={{ width: 1, flex: 1, background: "#e5e7eb", minHeight: 18 }} />
                )}
              </div>
              <div style={{ flex: 1, paddingBottom: 14, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{e.who}</div>
                <div style={{ fontSize: 12, color: m.color, fontWeight: 500 }}>{m.label}</div>
                {e.subject && (
                  <div
                    style={{
                      fontSize: 11.5,
                      color: "#6b7280",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {e.subject}
                  </div>
                )}
                <div style={{ fontSize: 11, color: "#9ca3af" }}>{e.when}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────── Hot vs cold patches block ─────────── */
function PatchesBlock({ isMobile, onContact }) {
  const patches = [
    { name: "Sevira / Banks", events: 14, kind: "hot", note: "Worth a door knock after Saturday's open." },
    { name: "Ridgeline / Maple", events: 9, kind: "hot", note: "Recent appraisals. Drop a hand-written note." },
    { name: "Heritage / Currawong", events: 4, kind: "warm", note: "Re-engage with a sold update." },
    { name: "Garrick / Highview", events: 1, kind: "cold", note: "Letter drop to wake the area up." },
  ];
  const color = (k) => (k === "hot" ? "#b91c1c" : k === "warm" ? "#b45309" : "#1d4ed8");
  const bg = (k) => (k === "hot" ? "#fee2e2" : k === "warm" ? "#fef3c7" : "#dbeafe");
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        border: "1px solid #e2e8f0",
        boxShadow: "0 14px 36px rgba(15,23,42,0.08)",
        padding: isMobile ? "1.25rem" : "1.75rem",
      }}
    >
      <div style={{ marginBottom: 14 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.8,
            color: BRAND_BLUE,
            textTransform: "uppercase",
            marginBottom: 6,
          }}
        >
          Your patch at a glance
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
          Hot streets to knock. Cold streets to drop.
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {patches.map((p) => (
          <div
            key={p.name}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 12px",
              background: "#f8fafc",
              borderRadius: 10,
              border: "1px solid #eef2f7",
            }}
          >
            <div
              style={{
                background: bg(p.kind),
                color: color(p.kind),
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.6,
                padding: "3px 8px",
                borderRadius: 5,
                textTransform: "uppercase",
                flexShrink: 0,
                minWidth: 48,
                textAlign: "center",
              }}
            >
              {p.kind}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>{p.name}</div>
              <div style={{ fontSize: 12, color: GRAY_DARK_COLOR }}>{p.note}</div>
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: color(p.kind),
                flexShrink: 0,
              }}
            >
              {p.events}
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={onContact}
        style={{
          marginTop: 16,
          width: "100%",
          background: BRAND_ACCENT_COLOR,
          color: "#fff",
          border: "none",
          padding: "0.75rem 1.25rem",
          borderRadius: 10,
          fontSize: 14,
          fontWeight: 700,
          cursor: "pointer",
          boxShadow: "0 6px 18px rgba(16,185,129,0.3)",
        }}
      >
        See your own map
      </button>
    </div>
  );
}

/* ─────────── Feature row ─────────── */
function FeatureRow({ icon, title, blurb, color, delay = 0 }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 14,
        padding: "0.7rem 0",
        animation: `mpFadeUp 0.6s ease both ${delay}s`,
      }}
    >
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: 11,
          background: `${color}1a`,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 18,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>
          {title}
        </div>
        <div style={{ fontSize: 13.5, color: GRAY_DARK_COLOR, lineHeight: 1.55 }}>{blurb}</div>
      </div>
    </div>
  );
}

/* ─────────── Page ─────────── */
function MapProspectorFeature() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );
  const [signInOpen, setSignInOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const wrap = {
    maxWidth: 1180,
    margin: "0 auto",
    padding: isMobile ? "0 1rem" : "0 2rem",
  };

  // Matches Landing's <Eyebrow> pill component
  const Eyebrow = ({ children }) => (
    <div
      style={{
        display: "inline-block",
        background: "rgba(16,185,129,0.1)",
        color: BRAND_ACCENT_COLOR,
        fontSize: "0.78rem",
        fontWeight: 700,
        borderRadius: "6px",
        padding: "0.3rem 0.75rem",
        marginBottom: "1rem",
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );

  // Matches Landing's section h2 style
  const h2 = {
    fontSize: isMobile ? "1.7rem" : "2.2rem",
    fontWeight: 700,
    color: BRAND_COLOR,
    margin: "0 0 1rem 0",
    lineHeight: 1.2,
    letterSpacing: "-0.01em",
  };

  const lead = {
    fontSize: isMobile ? 14.5 : 16,
    color: GRAY_DARK_COLOR,
    lineHeight: 1.65,
  };

  return (
    <div
      style={{
        background: "#f8fafc",
        minHeight: "100vh",
        color: "#0f172a",
        fontFamily: FONT_STACK,
      }}
    >
      <GlobalAnimations />
      <Navbar
        onSignIn={() => setSignInOpen(true)}
        onContactClick={() => setContactOpen(true)}
      />

      {/* ─── Hero ─── */}
      <section
        style={{
          background: DARK_GRADIENT,
          color: "#fff",
          padding: isMobile ? "3rem 0 3.5rem" : "5rem 0 5.5rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            pointerEvents: "none",
          }}
        />
        <div style={{ ...wrap, position: "relative", zIndex: 1 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "0.95fr 1.15fr",
              gap: isMobile ? 28 : 48,
              alignItems: "center",
            }}
          >
            <div style={{ animation: "mpFadeUp 0.6s ease both" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  background: "rgba(16, 185, 129, 0.15)",
                  border: "1px solid rgba(16, 185, 129, 0.3)",
                  color: BRAND_ACCENT_COLOR,
                  padding: "0.4rem 1rem",
                  borderRadius: 999,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  letterSpacing: "0.03em",
                  marginBottom: 18,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: BRAND_ACCENT_COLOR,
                    boxShadow: `0 0 0 4px ${BRAND_ACCENT_COLOR}33`,
                  }}
                />
                Map Prospector
              </div>
              <h1
                style={{
                  fontSize: isMobile ? "2.2rem" : "3.3rem",
                  fontWeight: 800,
                  lineHeight: 1.12,
                  letterSpacing: "-0.02em",
                  margin: 0,
                  marginBottom: 18,
                  color: "#fff",
                }}
              >
                See every contact engaging with you,{" "}
                <span style={{ color: BRAND_ACCENT_COLOR }}>on one map</span>.
              </h1>
              <p
                style={{
                  fontSize: isMobile ? "1.05rem" : "1.18rem",
                  lineHeight: 1.65,
                  color: "rgba(255,255,255,0.78)",
                  margin: 0,
                  marginBottom: 24,
                  maxWidth: 540,
                }}
              >
                Every property analysis, appraisal view, email open and click
                from your Insights Hub, pinned to the address it came from.
                Walk out of your open home and know exactly which doors to
                knock next.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  flexWrap: "wrap",
                  marginBottom: 20,
                }}
              >
                <button
                  onClick={() => setContactOpen(true)}
                  style={{
                    background: BRAND_ACCENT_COLOR,
                    color: "#fff",
                    border: "none",
                    padding: "0.85rem 1.7rem",
                    borderRadius: 8,
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: "pointer",
                    boxShadow: "0 4px 14px rgba(16,185,129,0.4)",
                    transition: "transform 0.15s, box-shadow 0.15s",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 6px 20px rgba(16,185,129,0.5)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(16,185,129,0.4)";
                  }}
                >
                  Book a demo
                </button>
                <button
                  onClick={() => setSignInOpen(true)}
                  style={{
                    background: "rgba(255,255,255,0.08)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.25)",
                    padding: "0.85rem 1.7rem",
                    borderRadius: 8,
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Sign in
                </button>
              </div>
            </div>
            <div style={{ animation: "mpFadeUp 0.7s ease both 0.1s" }}>
              <HeroMap isMobile={isMobile} />
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section: What you see on the map ─── */}
      <section style={{ padding: isMobile ? "3rem 0" : "5rem 0" }}>
        <div style={wrap}>
          <div style={{ textAlign: "center", marginBottom: isMobile ? 28 : 36, maxWidth: 720, marginLeft: "auto", marginRight: "auto" }}>
            <Eyebrow>Activity, geographically</Eyebrow>
            <h2 style={h2}>Your Insights Hub, pinned to a map.</h2>
            <p style={lead}>
              Every contact action becomes a pin on the address it came from.
              The badge tells you how many events have hit that address.
              Streets that light up are where your next listing lives.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1.1fr 1fr",
              gap: isMobile ? 28 : 40,
              alignItems: "center",
            }}
          >
            <div>
              <FeatureRow
                icon="📍"
                color={BRAND_ACCENT_COLOR}
                title="A pin per address"
                blurb="Anyone who analyses, views, opens or clicks gets pinned to the address they care about. The map clusters as you zoom out."
                delay={0}
              />
              <FeatureRow
                icon="🔴"
                color="#b91c1c"
                title="Event count badge"
                blurb="A red badge on the pin shows how many events that address has racked up. More events, more reason to follow up."
                delay={0.08}
              />
              <FeatureRow
                icon="🔍"
                color={BRAND_BLUE}
                title="Filter to what matters"
                blurb="Date range, campaign, event type, or a single email. Same filters as the Insights Hub, only spatial."
                delay={0.16}
              />
              <FeatureRow
                icon="🏠"
                color="#7c3aed"
                title="Built for open homes"
                blurb="Walk out of the open, pull up the map, and knock the doors that have been engaging this week. No spreadsheet, no guesswork."
                delay={0.24}
              />
            </div>

            <div>
              <PatchesBlock isMobile={isMobile} onContact={() => setContactOpen(true)} />
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section: Click a pin, get the timeline ─── */}
      <section
        style={{
          background: "#f1f5f9",
          padding: isMobile ? "3rem 0" : "5rem 0",
        }}
      >
        <div style={wrap}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1.1fr 1fr",
              gap: isMobile ? 28 : 48,
              alignItems: "center",
            }}
          >
            <div>
              <Eyebrow>Click any pin</Eyebrow>
              <h2 style={h2}>The full timeline, in one tap.</h2>
              <p style={{ ...lead, marginBottom: 20 }}>
                Tap a pin and the side panel slides out with every event for
                that address, in order. Who engaged. What they did. When.
                Everything you need before the call or the door knock.
              </p>
              <FeatureRow
                icon="🧭"
                color={BRAND_ACCENT_COLOR}
                title="Full event history"
                blurb="Property analyses, appraisal views, email opens, clicks, SMS, the lot. Nothing is hidden."
                delay={0}
              />
              <FeatureRow
                icon="📨"
                color={BRAND_BLUE}
                title="See the contact and the message"
                blurb="Which email subject they opened, which link they clicked. Open the email if you want to read what they read."
                delay={0.08}
              />
              <FeatureRow
                icon="🕒"
                color="#7c3aed"
                title="Newest first"
                blurb="A clean timeline, latest at the top. Hot leads jump straight out at you."
                delay={0.16}
              />
            </div>
            <div>
              <StaticTimelineCard a={ADDRESSES[0]} />
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section: How agents use it ─── */}
      <section style={{ padding: isMobile ? "3rem 0" : "5rem 0" }}>
        <div style={wrap}>
          <div
            style={{
              textAlign: "center",
              marginBottom: isMobile ? 28 : 36,
              maxWidth: 720,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <Eyebrow>How agents use it</Eyebrow>
            <h2 style={h2}>Stop prospecting blind.</h2>
            <p style={lead}>
              The list of who to call next has been hiding in plain sight on
              your patch. Map Prospector surfaces it.
            </p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
              gap: isMobile ? 16 : 24,
            }}
          >
            {[
              {
                icon: "🚪",
                color: "#b91c1c",
                t: "After the open home",
                b: "Open the map on your phone. The street is dotted with pins. Walk the block and door-knock the ones who've been engaging.",
              },
              {
                icon: "✉️",
                color: BRAND_BLUE,
                t: "Plan your letter drops",
                b: "Cold streets show up empty. That is where to drop. Warm the patch up and watch the pins appear over the next few weeks.",
              },
              {
                icon: "📞",
                color: BRAND_ACCENT_COLOR,
                t: "Call list, geographically",
                b: "Pick a suburb. See who has been busy this week. That is the morning call list, ranked by how warm they are.",
              },
            ].map((c, i) => (
              <div
                key={c.t}
                style={{
                  background: "#fff",
                  borderRadius: 14,
                  border: "1px solid #e2e8f0",
                  padding: "1.25rem 1.25rem 1.4rem",
                  boxShadow: "0 6px 18px rgba(15,23,42,0.05)",
                  animation: `mpFadeUp 0.6s ease both ${i * 0.06}s`,
                  transition: "transform 0.25s ease, box-shadow 0.25s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = "0 16px 36px rgba(15,23,42,0.12)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 6px 18px rgba(15,23,42,0.05)";
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: `${c.color}1a`,
                    color: c.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 20,
                    marginBottom: 12,
                  }}
                >
                  {c.icon}
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>
                  {c.t}
                </div>
                <div style={{ fontSize: 13.5, color: GRAY_DARK_COLOR, lineHeight: 1.55 }}>
                  {c.b}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section
        style={{
          background: DARK_GRADIENT,
          color: "#fff",
          padding: isMobile ? "3rem 0" : "5rem 0",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            pointerEvents: "none",
          }}
        />
        <div style={{ ...wrap, position: "relative", zIndex: 1 }}>
          <h2
            style={{
              fontSize: isMobile ? "1.7rem" : "2.2rem",
              fontWeight: 700,
              margin: "0 0 1rem 0",
              lineHeight: 1.2,
              letterSpacing: "-0.01em",
            }}
          >
            See your patch on the map.
          </h2>
          <p
            style={{
              fontSize: isMobile ? 14.5 : 17,
              color: "rgba(255,255,255,0.8)",
              maxWidth: 560,
              margin: "0 auto 26px",
              lineHeight: 1.65,
            }}
          >
            We will load your contacts, pin every event, and walk you through
            the hottest streets in a 20 minute call.
          </p>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => setContactOpen(true)}
              style={{
                background: BRAND_ACCENT_COLOR,
                color: "#fff",
                border: "none",
                padding: "0.95rem 1.85rem",
                borderRadius: 8,
                fontSize: 15.5,
                fontWeight: 600,
                cursor: "pointer",
                boxShadow: "0 10px 28px rgba(16,185,129,0.35)",
              }}
            >
              Book a demo
            </button>
            <button
              onClick={() => setSignInOpen(true)}
              style={{
                background: "rgba(255,255,255,0.08)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.25)",
                padding: "0.95rem 1.85rem",
                borderRadius: 8,
                fontSize: 15.5,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Sign in
            </button>
          </div>
        </div>
      </section>

      <SignInModal isOpen={signInOpen} onClose={() => setSignInOpen(false)} />
      <ContactVendorleadsModal isOpen={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}

export default MapProspectorFeature;
